import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { PageHead } from "@/components/hub/Shell";
import { Panel, Chips, Empty, StatStrip, Tb, Th, Td, CellMain, CellSub, St } from "@/components/portal/crm/kit";

const TONE = { undelivered: "attn", missing: "slate", invalid: "amber", landline: "amber", test: "slate", shared: "amber" };

/** Partner mobiles — who can't get or reply to a text yet, with the fix right on the row. */
export default function HubPartnerPhones() {
  const [data, setData] = useState(null);
  const [issue, setIssue] = useState("all");

  const load = useCallback(async () => {
    try { setData((await api.get("/hub/partner-phones")).data); }
    catch { setData({ items: [], counts: {}, labels: {} }); toast.error("Couldn't load partner mobiles"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (data?.items || []).filter((r) => issue === "all" || r.issue === issue), [data, issue]);
  if (!data) return <Empty testId="hub-phones-loading"><Loader2 className="animate-spin" /> Loading…</Empty>;
  const c = data.counts || {};
  const chips = [{ key: "all", label: `All (${data.items.length})` },
    ...Object.entries(data.labels || {}).filter(([k]) => c[k]).map(([k]) => ({ key: k, label: `${short(k)} (${c[k]})` }))];

  return (
    <div data-testid="hub-phones">
      <PageHead eyebrow="Desk tools" title="Partner mobiles" testId="hub-phones-head"
        sub="Partners texted from +44 7418 344101 can text straight back to Connie — but only from the mobile we hold. Fix the gaps here." />
      <StatStrip testId="hub-phones-stats" items={[
        ...(c.undelivered ? [{ k: "Not reaching", v: c.undelivered, h: "the network refused our texts", testId: "hub-phones-undelivered" }] : []),
        { k: "Ready for texts", v: data.ready, h: `of ${data.total_partners} active partners`, testId: "hub-phones-ready" },
        { k: "No mobile", v: c.missing || 0, h: "nothing saved yet", testId: "hub-phones-missing" },
        { k: "Can't take texts", v: (c.invalid || 0) + (c.landline || 0) + (c.test || 0), h: "invalid, landline or test number", testId: "hub-phones-bad" },
        { k: "Shared numbers", v: c.shared || 0, h: "Connie stays quiet on these", testId: "hub-phones-shared" },
      ]} />
      <div style={{ margin: "22px 0 16px" }}><Chips testPrefix="hub-phones-filter" value={issue} onChange={setIssue} items={chips} /></div>
      {rows.length === 0 ? (
        <Panel testId="hub-phones-empty"><Empty>{data.items.length ? "Nothing in this filter." : "Every active partner has a mobile we can text. Lovely."}</Empty></Panel>
      ) : (
        <Panel pad={false} testId="hub-phones-list" title={`${rows.length} partner${rows.length === 1 ? "" : "s"} to sort`}
               sub="Type the mobile and press Save — it's checked, saved in +44 format, and texts start coming from the reply number straight away.">
          <Tb head={<><Th>Partner</Th><Th>Type</Th><Th>What's wrong</Th><Th>Mobile</Th></>}>
            {rows.map((r) => <Row key={`${r.kind}:${r.id}`} r={r} onSaved={load} />)}
          </Tb>
        </Panel>
      )}
    </div>
  );
}

const short = (k) => ({ undelivered: "Not reaching", missing: "No mobile", invalid: "Invalid", landline: "Landline", test: "Test number", shared: "Shared" }[k] || k);

function Row({ r, onSaved }) {
  const [val, setVal] = useState(r.phone || "");
  const [busy, setBusy] = useState(false);
  const tid = `${r.kind}-${r.id}`;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/hub/partner-phones/${r.kind}/${r.id}`, { phone: val });
      if (data.issue) toast.warning(`Saved ${data.phone}`, { description: data.issue_label });
      else toast.success(`Saved ${data.phone}`, { description: `${r.company} can text Connie now.` });
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't save"); }
    finally { setBusy(false); }
  };
  return (
    <tr data-testid={`hub-phones-row-${tid}`}>
      <Td className="lead"><CellMain>{r.company}</CellMain><CellSub>{r.contact}{r.email ? ` · ${r.email}` : ""}</CellSub></Td>
      <Td>{r.label}</Td>
      <Td>
        <St tone={TONE[r.issue]} testId={`hub-phones-issue-${tid}`}>{r.issue_label}</St>
        {r.shared_with?.length > 0 && <CellSub style={{ margin: "5px 0 0" }}>Also on {r.shared_with.join(", ")}</CellSub>}
        {r.reason && <CellSub style={{ margin: "5px 0 0" }} data-testid={`hub-phones-reason-${tid}`}>Network said: {r.reason}</CellSub>}
      </Td>
      <Td>
        <form onSubmit={(e) => { e.preventDefault(); save(); }} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input className="inp" value={val} onChange={(e) => setVal(e.target.value)} placeholder="07700 900123"
                 style={{ width: 190 }} inputMode="tel" data-testid={`hub-phones-input-${tid}`} />
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !val.trim() || val === r.phone} data-testid={`hub-phones-save-${tid}`}>
            {busy ? <Loader2 className="animate-spin" /> : <><Check /> Save</>}
          </button>
        </form>
      </Td>
    </tr>
  );
}
